import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { POS_PERMISSIONS } from '@matix/types';
import { salesmenAPI } from '@matix/api-client';
import { useAuth } from '@matix/auth';
import { useCart } from '../../store/cart';
import { useCanI } from '../../lib/permissions';
import { useLang } from '../../lib/language';
import { nameFor } from '../../lib/localizedName';
import { UserIcon, XIcon } from '../../components/icons';

/**
 * "Change Salesman" — the legacy till's button above the cart.
 *
 * It shows who the receipt being rung up is credited to (the signed-in
 * cashier until someone else is picked). Clicking it lists the branch's
 * Cashier + Salesman users; the pick becomes the receipt's Empoyee_ID, is
 * printed as the cashier on the slip, and resets to the signed-in cashier
 * after the sale (cart reset), exactly as legacy's ClearCart did.
 *
 * Rendered only for a user holding `Change Salesman` — legacy did not render
 * the button without it, and the server refuses the field without it too.
 */
export function CreatorButton(): JSX.Element | null {
  const { t, lang } = useLang();
  const canChange = useCanI(POS_PERMISSIONS.ChangeSalesman);
  const cashier = useAuth().cashier;
  const creator = useCart((s) => s.creator);
  const setCreator = useCart((s) => s.setCreator);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const query = useQuery({
    queryKey: ['receiptCreators'],
    queryFn: () => salesmenAPI.creators(),
    enabled: canChange,
    staleTime: 5 * 60 * 1000,
  });

  const users = query.data?.users ?? [];
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.name.toLowerCase().includes(q) || (u.name_ar ?? '').toLowerCase().includes(q),
    );
  }, [users, filter]);

  if (!canChange || !cashier) return null;

  const selfName = nameFor(
    lang,
    { name: cashier.display_name ?? cashier.username ?? null, name_ar: cashier.display_name_ar ?? null },
    `#${cashier.sub}`,
  );
  const currentId = creator?.id ?? cashier.sub;
  const label =
    creator && creator.id !== cashier.sub
      ? nameFor(lang, { name: creator.name, name_ar: creator.nameAr }, `#${creator.id}`)
      : selfName;
  const isChanged = creator != null && creator.id !== cashier.sub;

  function pick(u: { id: number; name: string; name_ar: string | null }): void {
    // Picking yourself is the default — stored as null so nothing is sent.
    setCreator(u.id === cashier!.sub ? null : { id: u.id, name: u.name || null, nameAr: u.name_ar });
    setOpen(false);
    setFilter('');
  }

  return (
    <>
      <button
        type="button"
        className={`creator-btn${isChanged ? ' is-changed' : ''}`}
        onClick={() => setOpen(true)}
        title={t({ en: 'Change salesman', ar: 'تغيير البائع' })}
      >
        <UserIcon size={14} />
        <span className="creator-btn-label">{label}</span>
      </button>

      {open && (
        <div style={overlay} onClick={() => setOpen(false)}>
          <div style={panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div style={header}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{t({ en: 'Salesman', ar: 'البائع' })}</div>
              <button type="button" style={iconBtn} onClick={() => setOpen(false)} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
                <XIcon size={18} />
              </button>
            </div>
            <div style={{ padding: '12px 16px 0' }}>
              <input
                type="search"
                className="input"
                style={{ width: '100%' }}
                autoFocus
                placeholder={t({ en: 'Search by name', ar: 'ابحث بالاسم' })}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            <div style={body}>
              {query.isLoading ? (
                <Empty text={t({ en: 'Loading…', ar: 'جارٍ التحميل…' })} />
              ) : query.isError ? (
                <Empty text={t({ en: "Couldn't load the users.", ar: 'تعذّر تحميل المستخدمين.' })} />
              ) : shown.length === 0 ? (
                <Empty text={t({ en: 'No cashiers or salesmen on this branch.', ar: 'لا يوجد كاشير أو بائعون في هذا الفرع.' })} />
              ) : (
                shown.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    style={{ ...row, ...(u.id === currentId ? rowActive : {}) }}
                    onClick={() => pick(u)}
                  >
                    <span>{nameFor(lang, u, `#${u.id}`)}</span>
                    {u.id === cashier.sub && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted, #71717a)' }}>
                        {t({ en: 'you', ar: 'أنت' })}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Empty(props: { text: string }): JSX.Element {
  return (
    <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted, #71717a)' }}>{props.text}</div>
  );
}

// ── Inline styles (self-contained, same as the other cart modals) ─────────
const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
};
const panel: React.CSSProperties = {
  width: 'min(420px, 100%)', maxHeight: '80vh', display: 'flex', flexDirection: 'column',
  background: 'var(--surface-1, #fff)', color: 'var(--text, #18181b)',
  borderRadius: 12, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
};
const header: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 18px', borderBottom: '1px solid var(--border, #e4e4e7)',
};
const body: React.CSSProperties = {
  padding: 12, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6,
};
const iconBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: 4,
};
const row: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border, #e4e4e7)',
  background: 'transparent', cursor: 'pointer', textAlign: 'start', color: 'inherit', fontSize: 14,
};
const rowActive: React.CSSProperties = {
  borderColor: 'var(--accent, #2563eb)', background: 'var(--primary-light, #eff6ff)', fontWeight: 600,
};
